import React, { useMemo, useState } from "react";
import { Plus, Package } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// import UnitList from "./UnitList/UnitList";
// import UnitModal from "./UnitModal/UnitModal";
import "./UnitsPage.scss";
import { fetchUnitsList } from "../../Services/FetchUnitsList";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import UnitModal from "./UnitModal";
import { createUnit, deleteUnit, updateUnit } from "./UnitService";
import { AddAnElementButton } from "../../Components/AddElementsButton";
import {
  deleteAlert,
  errorAlert,
  handleSuccess,
  infoAlert,
  updateAlert,
} from "../../Components/Alerts";
import CrudPageHeader from "../../Components/CrudPageHeader";

export default function UnitsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);

  const queryClient = useQueryClient();

  // ------------------ React Query: Fetch Units (object form v5) ------------------
  const { data: units = [], isLoading } = useQuery({
    queryKey: ["units"],
    queryFn: fetchUnitsList,
    staleTime: 1000 * 60, // 1 minut caching
    onError: () => errorAlert("Njësitë matëse nuk mund të shfaqen"),
  });

  // ------------------ Mutation (object form v5) ------------------
  // UnitsPage.js - React Query Mutation
  const mutation = useMutation({
    mutationFn: ({ type, id, payload }) => {
      if (type === "create") return createUnit(payload);
      if (type === "update") return updateUnit(payload);
      if (type === "delete") return deleteUnit(id);
    },
    onSuccess: (message, variables) => {
      if (variables.type === "create")
        handleSuccess("Njësia u krijua me sukses!");
      if (variables.type === "update")
        updateAlert("Njësia u përditësua me sukses!");
      if (variables.type === "delete") deleteAlert(message);
      queryClient.invalidateQueries({ queryKey: ["units"] });
      setIsModalOpen(false);
    },
    onError: (error, variables) => {
      let action =
        variables.type === "create"
          ? "krijimi"
          : variables.type === "update"
            ? "përditësimi"
            : "fshirja";
      errorAlert(`Dështoi ${action}: ${error.message}`);
    },
  });

  // ------------------ Handlers ------------------
  const handleAddClick = () => {
    setEditingUnit(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (unit) => {
    setEditingUnit(unit);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (deleteItemId) => {
    mutation.mutate({ type: "delete", id: deleteItemId });
  };

  const handleSave = (data) => {
    mutation.mutate({
      type: editingUnit ? "update" : "create",
      id: editingUnit?.UnitID,
      payload: data,
    });
  };

  const unitGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 40,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "UnitName",
      headerName: "Emri i njesis",
      flex: 4.25,
      minWidth: 340,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Statusi",
      minWidth: 85,
      flex: 0.7,
      headerClassName: "super-app-theme--header",
      sortable: true,
      filterable: true,
      filterType: "select",
      filterOptions: [
        { value: "true", label: "Aktiv" },
        { value: "false", label: "Joaktiv" },
      ],
      renderCell: (params) =>
        params.value ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 20,
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.3px",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#10b981",
                display: "inline-block",
              }}
            />
            Aktiv
          </span>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 20,
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.3px",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#ef4444",
                display: "inline-block",
              }}
            />
            Joaktiv
          </span>
        ),
      groupable: false,
      aggregable: false,
    },
  ];

  const filteredData = useMemo(() => {
    let result = [...units];

    const quickText = (quickFilter || []).join(" ").trim().toLowerCase();
    if (quickText) {
      result = result.filter((item) =>
        [item.UnitName, item.IsActive ? "aktiv" : "joaktiv"]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(quickText)),
      );
    }

    if (Array.isArray(filterModel?.items)) {
      filterModel.items.forEach((filterItem) => {
        const field = filterItem?.field;
        const value = String(filterItem?.value ?? "").toLowerCase().trim();
        if (!field || value === "") {
          return;
        }

        result = result.filter((item) => {
          const raw = item?.[field];
          if (field === "IsActive") {
            const normalized = String(Boolean(raw)).toLowerCase();
            return normalized === value;
          }
          return String(raw ?? "").toLowerCase().includes(value);
        });
      });
    }

    if (Array.isArray(sortModel) && sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      if (field && sort) {
        result.sort((a, b) => {
          const aValue = a?.[field];
          const bValue = b?.[field];

          const aComparable = typeof aValue === "number" || typeof aValue === "boolean"
            ? Number(aValue)
            : String(aValue ?? "");
          const bComparable = typeof bValue === "number" || typeof bValue === "boolean"
            ? Number(bValue)
            : String(bValue ?? "");

          if (aComparable < bComparable) {
            return sort === "asc" ? -1 : 1;
          }
          if (aComparable > bComparable) {
            return sort === "asc" ? 1 : -1;
          }
          return 0;
        });
      }
    }

    return result;
  }, [units, filterModel, quickFilter, sortModel]);

  const rowCount = filteredData.length;
  const pagedData = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return filteredData.slice(start, start + paginationModel.pageSize);
  }, [filteredData, paginationModel]);

  const rows = pagedData.map((item, index) => ({
    id: paginationModel.page * paginationModel.pageSize + index + 1,
    UnitName: item.UnitName,
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.UnitID,
    rowInfo: item
  }));

  return (
    <div className="UP__page">

      <CrudPageHeader
        icon={Package}
        title="Njesitë matëse"
        description="Menaxhoni, shtoni, modifikoni dhe fshini njesitë matëse në mënyrë efikase."
        tutorialLabel="Shiko Tutorialin"
        onTutorialClick={() => infoAlert("Tutorial së shpejti!")}
        action={
          <AddAnElementButton
            className="UP__btn-primary"
            text="Njesi e Re"
            icon={<Plus size={16} />}
            onClick={handleAddClick}
          />
        }
        className="UP__header"
      />

      <ReusableServerDataGrid
        columns={unitGridHeader}
        rows={rows}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        showViewAction={false}
        showPayment={false}
        actionsMinWidth={78}
        actionsFlex={0.6}
        height={500}
        rowCount={rowCount}
        loading={isLoading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
      />

      {/* Modal */}
      <UnitModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        initialData={editingUnit}
      />
    </div>
  );
}
